/**
 * @file recordingViewer.ts
 * @brief Whether the recording setup shows the company transparency line.
 *
 * @details
 * Sharing v2 (DES 21-10-2026, decision 5): on a company account, members see
 * "Your company admins can see this meeting and who it is shared with." on
 * the Boardroom and Notes setup. Admins and personal accounts see nothing.
 * The server decides: `GET /api/auth/me` returns top-level `viewer` flags
 * (`companyAdminsCanSee` for Boardroom, `companyAdminsCanSeeNotes` for Notes,
 * false when the Notes switch is off). An older server sends none: no line.
 *
 * One cached /me read shared with the data controls (shared/auth/meExtras.ts);
 * no vendor cost.
 *
 * @references
 * - BACKEND auth/routes.js GET /me (recordingViewerFlags)
 * - scratch/claude-code-report.md "Sharing v2: Part 1" section 1
 *
 * @author DES
 * @date 21-10-2026
 */

import { useMeExtras, type RecordingViewerFlags } from "@/shared/auth/meExtras";

export type { RecordingViewerFlags };

export const COMPANY_ADMINS_CAN_SEE_LINE =
  "Your company admins can see this meeting and who it is shared with.";

/** The `viewer` flags from the shared /me extras read (shared/auth/meExtras). */
export function useRecordingViewerFlags(_userId?: string | null) {
  const q = useMeExtras();
  return { ...q, data: q.data?.viewer ?? null };
}

/** The line for this tier, or null (admins, personal accounts, old servers). */
export function transparencyLine(
  flags: RecordingViewerFlags | null | undefined,
  tier: "notes" | "full",
): string | null {
  if (!flags) return null;
  const on = tier === "notes" ? flags.companyAdminsCanSeeNotes : flags.companyAdminsCanSee;
  return on ? COMPANY_ADMINS_CAN_SEE_LINE : null;
}
